/* oxlint-disable */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { Account } from '@/features/dashboard/interface';
import { CryptoWallet } from '@/features/v2/deposit-withdrawals/withdrawals/components/interfaces';

import { clearFingerprintCaptured } from '@/utils/fingerprintStorage';

// Narrow DTO for hydrating the account from the authenticated user. Kept
// local to this store on purpose: userStore must never import Better Auth's
// `Session` type — it only needs a handful of fields to seed `account`.
export interface AuthUserDTO {
  id: string | number;
  email: string;
  name?: string | null;
  lastName?: string | null;
  nick?: string | null;
  phone?: string | null;
  parent?: number | null;
  emailVerified: boolean;
  createdAt?: Date | string | null;
  currency?: string | null;
  country?: string | null;
}

export interface UserAccount {
  address: string | undefined;
  id: number | undefined;
  bornDay: string | undefined;
  city: string | undefined;
  email: string | undefined;
  typeBonusSelected: string | undefined;
  emailVerified: boolean | undefined;
  firstDeposit: boolean | undefined;
  fullProfile: boolean | undefined;
  kycLevel: number | undefined;
  kycStatus: number | undefined;
  kycNotes: string | undefined;
  lastName: string | undefined;
  name: string | undefined;
  nick: string | undefined;
  phone: string | undefined;
  provider: number | undefined;
  parent: number | undefined;
  topup_id: string | undefined;
  dni: string | undefined;
  favoriteGames: string[] | undefined;
  favoriteClub: string | undefined;
  favoriteSports: string[] | undefined;
  gender: number | undefined;
  lastDeposit: Date | undefined;
  haveDeposit: boolean | undefined;
  contingencies: {
    casino: boolean | undefined;
    bets: boolean | undefined;
    deposits: boolean | undefined;
    withdrawals: boolean | undefined;
  },
  vouchers: string | undefined;
  createdAt: Date | undefined;
  fingerprintCaptured: boolean | undefined;
  soliticsInitialized: boolean | undefined;
  currency: string | undefined;
  country: string | undefined; // Country code (GT, DO, GLOBAL, etc.)
  onboardingCompleted: boolean | undefined;
  bettingPreferences: string[] | undefined;
  sportsbookOddsFormat: string | undefined;
}

const AccountInitialState: UserAccount = {
  address: undefined,
  id: undefined,
  bornDay: undefined,
  city: undefined,
  email: undefined,
  typeBonusSelected: undefined,
  emailVerified: undefined,
  firstDeposit: undefined,
  fullProfile: undefined,
  kycLevel: undefined,
  kycStatus: undefined,
  kycNotes: undefined,
  lastName: undefined,
  name: undefined,
  nick: undefined,
  phone: undefined,
  provider: undefined,
  parent: undefined,
  topup_id: undefined,
  dni: undefined,
  favoriteGames: undefined,
  favoriteClub: undefined,
  favoriteSports: undefined,
  gender: undefined,
  lastDeposit: undefined,
  haveDeposit: undefined,
  contingencies: {
    casino: undefined,
    bets: undefined,
    deposits: undefined,
    withdrawals: undefined,
  },
  vouchers: undefined,
  createdAt: undefined,
  fingerprintCaptured: undefined,
  soliticsInitialized: undefined,
  currency: undefined,
  country: undefined,
  onboardingCompleted: undefined,
  bettingPreferences: undefined,
  sportsbookOddsFormat: undefined,
};

export interface AuthState {
  account: UserAccount;
  setAccount: (data: UserAccount) => void;

  // Hydrates `account` from the currently authenticated user. Auth session
  // lifecycle itself lives in Better Auth (see core/auth/client/auth-client.ts);
  // this store only cares about the application-level account projection.
  updateAccountFromAuthUser: (user: AuthUserDTO) => void;

  typeBonusSelected: string | undefined;
  setTypeBonusSelected: (type: string) => void;

  accounts: Account[];
  addAccount: (currentAccount: Account[]) => void;
  clearAccounts: () => void;
  removeAccount: (token: string) => void;

  cryptoWallets: CryptoWallet[];
  addCryptoWallet: (currentWallets: CryptoWallet[]) => void;
  clearCryptoWallets: () => void;
  removeCryptoWallet: (token: string) => void;
  selectCryptoWallet: (wallet: CryptoWallet | null) => void;
  selectedCryptoWallet: CryptoWallet | null;
  clearSelectedCryptoWallet: () => void;

  balance: any[];
  setBalance: (value: any[]) => void;

  isxtremepushSet: boolean;
  setXtremepush: () => void;

  bonuses: {
    kycStatus: number | undefined;
    kycLevel: number | undefined;
    fullProfile: boolean | undefined;
    sport: any[];
    spins: any[];
  };
  setBonuses: (bonuses: { sport: any[]; spins: any[] }) => void;

  clearStore: () => void;

  notifyAccepted: boolean;
  setNotifyAccepted: () => void;
  selectAccount: (account: Account | null) => void;
  selectedAccount: Account | null;
  clearSelectedAccount: () => void;

  btnStateMetaMap: {
    status: boolean;
    date: Date | null;
  };
  setBtnStateMetaMap: (btnStateMetaMap: { status: boolean; date: Date | null }) => void;

  updateFavoriteGames: (favoriteGames: string[]) => void;

  vouchers: string | undefined;
  setVouchers: (vouchers: string | undefined) => void;

  gateways: {
    payphone: boolean | undefined;
    unlimit: boolean | undefined;
    ginih: boolean | undefined;
    vouchers: boolean | undefined;
    euka: boolean | undefined;
    tenCoins: boolean | undefined;
    google: boolean | undefined;
    apple: boolean | undefined;
    praxis: boolean | undefined;
    v2: boolean | undefined;
    praxisWithdrawal: boolean | undefined;
  },
  setGateways: (gateways: { payphone: boolean | undefined; unlimit: boolean | undefined; ginih: boolean | undefined; vouchers: boolean | undefined; euka: boolean | undefined; tenCoins: boolean | undefined; google: boolean | undefined; apple: boolean | undefined; praxis: boolean | undefined; v2: boolean | undefined; praxisWithdrawal: boolean | undefined; }) => void;

  // Fingerprint management
  setFingerprintCaptured: () => void;
  isFingerprintCaptured: () => boolean;

  // Solitics management
  setSoliticsInitialized: () => void;
  isSoliticsInitialized: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      return {
        // To current user account
        account: AccountInitialState,
        setAccount: (data: UserAccount) => { set({ account: data }); },
        updateFavoriteGames: (favoriteGames: string[]) => {
          set({ account: { ...get().account, favoriteGames } });
        },
        // End, current user account

        // Account hydration from authenticated user
        updateAccountFromAuthUser: (user: AuthUserDTO) => {
          if (!user) return;

          // Convert string ID to number as Better Auth keeps IDs as strings for compatibility
          const updatedAccount: UserAccount = {
            ...get().account,
            id: Number(user.id),
            email: user.email,
            name: user.name ?? undefined,
            lastName: user.lastName ?? undefined,
            nick: user.nick ?? undefined,
            phone: user.phone ?? undefined,
            parent: user.parent ?? undefined,
            emailVerified: user.emailVerified,
            createdAt: user.createdAt ? new Date(user.createdAt) : undefined,
            currency: user.currency ?? 'USD',
            country: user.country ?? 'GLOBAL',
          };
          set({ account: updatedAccount });
        },
        // End, account hydration from authenticated user

        // To typeBonusSelected
        typeBonusSelected: undefined,
        setTypeBonusSelected: (type: string) => { set({ typeBonusSelected: type }); },
        // End, to typeBonusSelected

        // To accounts
        accounts: [],
        addAccount: (currentAccount: Account[]) => {
          return set({ accounts: currentAccount });
        },
        clearAccounts: () => {
          return set({ accounts: [] });
        },
        removeAccount: (token: string) => {
          const accounts: Account[] = get().accounts.filter((account) => {
            return (account.token !== token);
          });
          set({ accounts });
        },
        // End, to accounts

        // To crypto wallets
        cryptoWallets: [],
        addCryptoWallet: (currentWallets: CryptoWallet[]) => {
          return set({ cryptoWallets: currentWallets });
        },
        clearCryptoWallets: () => {
          return set({ cryptoWallets: [] });
        },
        removeCryptoWallet: (token: string) => {
          const cryptoWallets: CryptoWallet[] = get().cryptoWallets.filter((wallet) => {
            return (wallet.token !== token);
          });
          set({ cryptoWallets });
        },
        selectCryptoWallet: (wallet: CryptoWallet | null) => {
          if (wallet) {
            return set({ selectedCryptoWallet: wallet });
          }
          return set({ selectedCryptoWallet: null });
        },
        selectedCryptoWallet: null,
        clearSelectedCryptoWallet: () => {
          return set({ selectedCryptoWallet: null });
        },
        // End, to crypto wallets

        // To balance
        balance: [],
        setBalance: (value: any[]) => { set({ balance: value }); },
        // End, to balance

        // To isxtremepushSet
        isxtremepushSet: false,
        setXtremepush: () => { set({ isxtremepushSet: true }); },
        // End, to isxtremepushSet

        // To bonuses
        bonuses: {
          kycStatus: undefined,
          kycLevel: undefined,
          fullProfile: undefined,
          sport: [],
          spins: [],
        },
        setBonuses: (bonuses: {
          sport: any[];
          spins: any[];
          kycStatus?: number;
          kycLevel?: number;
          fullProfile?: boolean;
        }) => {
          set({
            bonuses: {
              ...get().bonuses,
              ...bonuses,
            },
          });
        },
        // End, to bonuses

        // To clear store
        clearStore: () => {
          // Clear the persisted data from localStorage
          localStorage.removeItem('IT_account');
          // Clear fingerprint data from localStorage
          clearFingerprintCaptured();
          // Clear coming-soon bypass status
          if (typeof window !== 'undefined') {
            localStorage.removeItem('powerbet_bypass_coming_soon');
            document.cookie = 'powerbet_bypass_coming_soon=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          }
          // Reset all state to initial values
          set({
            account: {
              ...AccountInitialState,
              fingerprintCaptured: false,
              soliticsInitialized: false,
            },
            typeBonusSelected: undefined,
            accounts: [],
            cryptoWallets: [],
            selectedCryptoWallet: null,
            balance: [],
            bonuses: {
              kycStatus: undefined,
              kycLevel: undefined,
              fullProfile: undefined,
              sport: [],
              spins: [],
            },
            gateways: undefined,
            btnStateMetaMap: {
              status: false,
              date: null,
            },
            vouchers: undefined,
            isxtremepushSet: false,
            notifyAccepted: false,
            selectedAccount: null,
          });
        },
        // End, to clear store

        // To notifyAccepted
        notifyAccepted: false,
        setNotifyAccepted: () => { set({ notifyAccepted: true }); },
        // End, to notifyAccepted
        selectAccount: (account: Account | null) => {
          if (account) {
            return set({ selectedAccount: account });
          }
          return set({ selectedAccount: null });
        },
        selectedAccount: null,
        clearSelectedAccount: () => {
          return set({ selectedAccount: undefined });
        },

        btnStateMetaMap: {
          status: false,
          date: null,
        },
        setBtnStateMetaMap: (btnStateMetaMap: { status: boolean; date: Date | null }) => { return set(() => { return { btnStateMetaMap }; }); },

        vouchers: undefined,
        setVouchers: (vouchers: string | undefined) => { set({ vouchers }); },

        gateways: {
          payphone: undefined,
          unlimit: undefined,
          ginih: undefined,
          vouchers: undefined,
          euka: undefined,
          tenCoins: undefined,
          google: undefined,
          apple: undefined,
          praxis: undefined,
          v2: undefined,
          praxisWithdrawal: undefined,
        },
        setGateways: (gateways: { payphone: boolean | undefined; unlimit: boolean | undefined; ginih: boolean | undefined; vouchers: boolean | undefined; euka: boolean | undefined; tenCoins: boolean | undefined; google: boolean | undefined; apple: boolean | undefined; praxis: boolean | undefined; v2: boolean | undefined; praxisWithdrawal: boolean | undefined; }) => { set({ gateways }); },

        // Fingerprint management
        setFingerprintCaptured: () => { set({ account: { ...get().account, fingerprintCaptured: true } }); },
        isFingerprintCaptured: () => { return get().account.fingerprintCaptured === true; },

        // Solitics management
        setSoliticsInitialized: () => { set({ account: { ...get().account, soliticsInitialized: true } }); },
        isSoliticsInitialized: () => { return get().account.soliticsInitialized === true; },
      };
    },
    {
      name: 'IT_account',
      storage: createJSONStorage(() => { return localStorage; }),
    },
  ),
);
